import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaigns, createCampaign, sendCampaign } from '../store/slices/campaignSlice';
import { fetchTemplates } from '../store/slices/templateSlice';
import { toast } from 'react-toastify';
import { PlusIcon, PaperAirplaneIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import { COUNTRIES } from '../utils/countries';
import api from '../utils/api';

const CATEGORIES = [
  { value: 'Schools', label: 'Schools' },
  { value: 'College', label: 'College' },
  { value: 'Universities', label: 'Universities' },
  { value: 'Government Departments', label: 'Government Departments' },
  { value: 'Ministries', label: 'Ministries' },
  { value: 'Embassies & High Commissions', label: 'Embassies & High Commissions' },
  { value: 'NGOs & Foundations', label: 'NGOs & Foundations' },
  { value: 'Education Boards', label: 'Education Boards' },
  { value: 'Corporate Partners', label: 'Corporate Partners' },
  { value: 'Individual Officials', label: 'Individual Officials (Ambassadors, Education Officers, Directors)' },
  { value: 'Other', label: 'Other' }
];

const Campaigns = () => {
  const dispatch = useDispatch();
  const { items, loading } = useSelector((state) => state.campaigns);
  const { items: templates } = useSelector((state) => state.templates);
  const { user } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contactCount, setContactCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingId, setIsSendingId] = useState(null);

  useEffect(() => {
    dispatch(fetchCampaigns());
    dispatch(fetchTemplates());
  }, [dispatch]);

  // Fetch contact count when filters change
  useEffect(() => {
    if (showModal && (selectedCategory || selectedCountry)) {
      fetchContactCount();
    } else if (showModal) {
      setContactCount(0);
    }
  }, [selectedCategory, selectedCountry, showModal]);

  const fetchContactCount = async () => {
    setLoadingCount(true);
    try {
      const params = new URLSearchParams();

      if (selectedCategory) {
        params.append('categories[]', selectedCategory.value);
      }

      if (selectedCountry) {
        params.append('countries[]', selectedCountry.value);
      }

      const response = await api.get(`/contacts/count?${params.toString()}`);
      setContactCount(response.data.count);
    } catch (error) {
      console.error('Error fetching contact count:', error);
      setContactCount(0);
    } finally {
      setLoadingCount(false);
    }
  };

  const handlePreviewTemplate = () => {
    if (!selectedTemplate) {
      toast.error('Please select a template first');
      return;
    }
    const template = templates.find(t => t.id === parseInt(selectedTemplate.value));
    if (template) {
      setPreviewTemplate(template);
      setShowPreview(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (contactCount === 0) {
      toast.error('No contacts match the selected filters');
      return;
    }

    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    setIsSubmitting(true);
    try {
      const filters = {};
      if (selectedCategory) {
        filters.categories = [selectedCategory.value];
      }
      if (selectedCountry) {
        filters.countries = [selectedCountry.value];
      }

      const campaignData = {
        name: campaignName,
        templateId: selectedTemplate.value,
        filters,
        totalRecipients: contactCount
      };

      await dispatch(createCampaign(campaignData)).unwrap();
      toast.success('Campaign created successfully');
      setShowModal(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSend = async (id) => {
    if (isSendingId) return;

    if (window.confirm('Send this campaign to all matching contacts?')) {
      setIsSendingId(id);
      try {
        await dispatch(sendCampaign({ id })).unwrap();
        toast.success('Campaign sent successfully');
      } catch (err) {
        toast.error('Failed to send campaign');
      } finally {
        setIsSendingId(null);
      }
    }
  };

  const resetForm = () => {
    setCampaignName('');
    setSelectedCategory(null);
    setSelectedCountry(null);
    setSelectedTemplate(null);
    setContactCount(0);
  };

  const canCreate = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canSend = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);

  const templateOptions = templates.map(t => ({ value: t.id, label: t.name }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Campaigns</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 font-medium">Send emails to specific contact groups</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Campaign
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Recipients</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Sent</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Failed</th>
                  {canSend && <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{campaign.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ${campaign.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        campaign.status === 'sending' ? 'bg-blue-100 text-blue-800' :
                          campaign.status === 'sent' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                        }`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{campaign.totalRecipients || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{campaign.sentCount || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{campaign.failedCount || 0}</td>
                    {canSend && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => handleSend(campaign.id)}
                            disabled={isSendingId === campaign.id}
                            className="flex items-center px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSendingId === campaign.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1"></div>
                            ) : (
                              <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                            )}
                            {isSendingId === campaign.id ? 'Sending...' : 'Send'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => { setShowModal(false); resetForm(); }}></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                  <h3 className="text-2xl font-bold text-white">Send Email Campaign</h3>
                </div>

                <div className="bg-white px-6 py-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name *</label>
                      <input
                        type="text"
                        required
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., December Newsletter"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Category</label>
                        <Select
                          options={CATEGORIES}
                          value={selectedCategory}
                          onChange={setSelectedCategory}
                          placeholder="Select category..."
                          isClearable
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                        <p className="text-xs text-gray-500 mt-1">Filter by contact type</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                        <Select
                          options={COUNTRIES}
                          value={selectedCountry}
                          onChange={setSelectedCountry}
                          placeholder="Select country..."
                          isClearable
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                        <p className="text-xs text-gray-500 mt-1">Filter by country</p>
                      </div>
                    </div>

                    <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-blue-900">Matching Contacts</p>
                          <p className="text-xs text-blue-700 mt-1">
                            {!selectedCategory && !selectedCountry
                              ? 'Select category or country to see contacts'
                              : 'Contacts matching your selection'}
                          </p>
                        </div>
                        <div className="text-right">
                          {loadingCount ? (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          ) : (
                            <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{contactCount}</p>
                          )}
                          <p className="text-xs text-blue-700 font-medium">recipients</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Email Template *</label>
                      <div className="flex gap-3">
                        <Select
                          options={templateOptions}
                          value={selectedTemplate}
                          onChange={setSelectedTemplate}
                          placeholder="Choose template..."
                          className="flex-1 react-select-container"
                          classNamePrefix="react-select"
                          required
                        />
                        <button
                          type="button"
                          onClick={handlePreviewTemplate}
                          disabled={!selectedTemplate}
                          className="flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg"
                        >
                          <EyeIcon className="h-5 w-5 mr-1" />
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting || contactCount === 0 || !selectedTemplate}
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                    {isSubmitting ? 'Creating...' : 'Create Campaign'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowPreview(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">Template Preview</h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <span className="font-medium text-sm text-gray-600 mr-2">Subject:</span>
                      <span className="text-lg font-semibold text-gray-900">{previewTemplate.subject}</span>
                    </div>
                  </div>

                  <div className="bg-white p-6 max-h-[50vh] overflow-y-auto">
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewTemplate.htmlContent }}
                    />

                    {previewTemplate.footer && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="text-sm text-gray-600 whitespace-pre-wrap">
                          {previewTemplate.footer}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowPreview(false)}
                  className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
